import React, { useEffect, useMemo, useState } from "react";

import { MRT_ColumnDef, useMaterialReactTable } from "material-react-table";

import { useActionHook } from "../../../hooks";
import { useAccountSelector, useReportsActions, useReportsQueriesSelector } from "../../../store";
import { ClientDealTableRow } from "../../../store/types";
import { SelectOption } from "../../forms";
import { TableCell } from "../Measures-table/TableCell";
import { HedgeTableToolbar } from "./HedgeFundTableToolbar";
import { tableProps, titleColumn } from "./ReportMeasuresTable";
import { TableToolbar } from "./TableToolbar";

type Props = { groupByOptions: SelectOption[]; shouldFetchData: boolean };

export const useHedgeFundTable = ({ groupByOptions, shouldFetchData }: Props) => {
  const { hedge_fund } = useAccountSelector() || {};

  const [monthlyViewGroupBy, setMonthlyViewGroupBy] = useState<string>("none");
  const [monthlyViewReportBy, setMonthlyViewReportBy] = useState<"deal" | "year">("year");

  const { getHedgeFundTableActon, setIsHedgeFundsAction } = useReportsActions();
  const { filters, use_accounting_date } = useReportsQueriesSelector();

  const {
    action: getHedgeFundsTable,
    loading: hedgeFundsLoading,
    data: hedgeFundsData
  } = useActionHook((queries: Parameters<typeof getHedgeFundTableActon>[0]) =>
    shouldFetchData ? getHedgeFundTableActon(queries) : undefined
  );

  useEffect(() => {
    const reportByInvert = monthlyViewReportBy === "deal" ? "year" : "deal";
    getHedgeFundsTable({
      filters,
      groupBy: monthlyViewGroupBy === "none" ? [[reportByInvert]] : [[monthlyViewGroupBy], [reportByInvert]],
      use_accounting_date,
      rowBy: monthlyViewReportBy
    });
  }, [monthlyViewGroupBy, monthlyViewReportBy, filters, use_accounting_date, shouldFetchData]);

  const measureTableColumns = useMemo(() => {
    if (!hedgeFundsData) {
      return [];
    }
    const columns: MRT_ColumnDef<ClientDealTableRow>[] = hedgeFundsData.columns.map<MRT_ColumnDef<ClientDealTableRow>>(
      (col) => {
        return {
          header: col.title,
          id: col.key,
          accessorKey: col.key,
          sortingFn: "basic",
          accessorFn: (row) => row.valueData[col.key],
          Cell: ({ row, column }) => (
            <TableCell
              id={column.id}
              value={row.original.valueData[col.key]}
              postfix={row.original.postfixes?.[col.key] || col.postfix || row.original.postfix}
            />
          ),
          AggregatedCell: ({ row, column }) => (
            <TableCell
              id={column.id}
              value={row.original.valueData[col.key]}
              postfix={row.original.postfixes?.[col.key] || col.postfix || row.original.postfix}
              aggregated
            />
          )
        };
      }
    );

    columns.unshift(titleColumn);

    return columns;
  }, [hedgeFundsData]);

  const table = useMaterialReactTable<ClientDealTableRow>({
    // Common table Props
    ...tableProps,
    getRowId: (row) => row.id!,
    getSubRows: (row) => row.children,
    filterFromLeafRows: true,
    enableColumnActions: false,
    enableBottomToolbar: false,
    layoutMode: "grid",
    // Specific table Props
    columns: measureTableColumns,
    enableColumnResizing: true,

    data: hedgeFundsData?.rows || [],

    // This are styles for "monthly_view" to make columns be flexible
    columnResizeMode: "onEnd",
    defaultColumn: {
      ...tableProps.defaultColumn,
      size: 10, // To get rid off minimum columns size
      muiTableBodyCellProps: {
        sx: { padding: "8px 12px" }
      },
      muiTableHeadCellProps: {
        sx: { padding: "8px 12px" }
      }
    },

    enableExpanding: true,
    state: { isLoading: hedgeFundsLoading },
    renderTopToolbar: (props) => {
      return (
        <TableToolbar
          setFullScreen={() => props.table.setIsFullScreen(!props.table.getState().isFullScreen)}
          isFullScreen={props.table.getState().isFullScreen}
          {...(hedge_fund && { tabs: { value: "monthly_view", onChange: () => setIsHedgeFundsAction(false) } })}
        >
          <HedgeTableToolbar
            groupByOptions={groupByOptions}
            monthlyViewGroupBy={monthlyViewGroupBy}
            setMonthlyViewReportBy={setMonthlyViewReportBy}
            monthlyViewReportBy={monthlyViewReportBy}
            setMonthlyViewGroupBy={setMonthlyViewGroupBy}
          />
        </TableToolbar>
      );
    }
  });

  return table;
};
